import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { motion } from "framer-motion";
import {
  Activity,
  Award,
  Compass,
  Copy,
  Crown,
  ExternalLink,
  Layers,
  Mail,
  MapPin,
  Medal,
  Moon,
  Phone,
  Radar,
  Satellite,
  Search,
  Sparkles,
  Star,
  Sun,
  Target,
  TrendingDown,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * ApplicantHeatmap 3.0 — the "peak UX" cinematic redesign.
 *
 * What's new vs 2.0 (all shipped together):
 *   • Editorial hero band with a huge DM-serif total + prose supporting text
 *   • KPI band recast as inline chips instead of boxed tiles for a lighter feel
 *   • Toolbar grouped into semantic clusters (view · signal · quality · nav)
 *   • Map presented as a hero with a warm oxblood/gold gradient frame + shadow
 *   • Sidebar redesigned around three editorial panels:
 *       ① MARKET RANKINGS with gold/silver/bronze medals for the top 3
 *       ② COVERAGE GAPS with days-since counters + zoom shortcuts
 *       ③ RECENT PULSE — a live feed of the last 7 days of newcomers
 *   • Framer-motion staggered entrance across every module
 *   • Glass-morphism popup + custom typography inside every marker popup
 *
 * Every data-testid from the previous version is preserved so the frontend
 * tests keep passing.
 */
const REGIONS = ["North Carolina", "South Carolina", "Georgia", "Tennessee", "Virginia"];

const STATE_CODES = {
  "North Carolina": "NC",
  "South Carolina": "SC",
  "Georgia":        "GA",
  "Tennessee":      "TN",
  "Virginia":       "VA",
};

// Below this zoom threshold we render cluster pips (color-first, no
// numbers, non-interactive) so the heat layer's *color* carries the
// story. At/above it we render the beautiful per-city numbered markers.
const HIGH_DETAIL_ZOOM = 8;

const REGION_VIEWS = {
  "North Carolina": { center: [35.4, -79.5], zoom: 7 },
  "South Carolina": { center: [34.0, -81.0], zoom: 7 },
  "Georgia":        { center: [32.9, -83.5], zoom: 7 },
  "Tennessee":      { center: [35.9, -86.5], zoom: 7 },
  "Virginia":       { center: [37.7, -78.5], zoom: 7 },
  "Southeast":      { center: [34.5, -81.5], zoom: 6 },
};

const BASEMAPS = {
  light: {
    url: "https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png",
    attribution: "© OSM · © CARTO",
    icon: Sun,
    label: "Light",
  },
  dark: {
    url: "https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png",
    attribution: "© OSM · © CARTO",
    icon: Moon,
    label: "Dark",
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "© Esri",
    icon: Satellite,
    label: "Satellite",
  },
};

const MILES_TO_METERS = 1609.34;

// ─── Coverage tiers ─────────────────────────────────────────────────────
// True heatmap ramp — red = most saturated / densest coverage.
//   1-9    →  GREY · sparse
//   10-24  →  GREEN · starting
//   25-49  →  YELLOW · decent
//   50-99  →  RED · strong
//   100+   →  DEEP RED · solid, saturated peak
//   0      →  non-existent (nothing rendered)
const COVERAGE_TIERS = [
  { min: 100, label: "SOLID",    tag: "100+",  fill: "#7F1D1D", glow: "rgba(127,29,29,0.75)" },
  { min: 50,  label: "STRONG",   tag: "50-99", fill: "#DC2626", glow: "rgba(220,38,38,0.65)" },
  { min: 25,  label: "DECENT",   tag: "25-49", fill: "#CA8A04", glow: "rgba(202,138,4,0.65)" },
  { min: 10,  label: "STARTING", tag: "10-24", fill: "#15803D", glow: "rgba(21,128,61,0.65)" },
  { min: 1,   label: "SPARSE",   tag: "1-9",   fill: "#6B7280", glow: "rgba(107,114,128,0.60)" },
];
function tierFor(count) {
  if (!count || count <= 0) return null;
  for (const t of COVERAGE_TIERS) if (count >= t.min) return t;
  return COVERAGE_TIERS[COVERAGE_TIERS.length - 1];
}

// Curated Southeast metro anchors (Reelty's active + adjacent target markets).
// Used to surface *local* coverage gaps — the empty space between the cities
// we already have applicants in — instead of the coarse state-level view.
const SE_METROS = [
  // North Carolina
  { name: "Charlotte",      county: "Mecklenburg",         state: "NC", region: "North Carolina", lat: 35.2271, lng: -80.8431 },
  { name: "Raleigh",        county: "Wake",                state: "NC", region: "North Carolina", lat: 35.7796, lng: -78.6382 },
  { name: "Durham",         county: "Durham",              state: "NC", region: "North Carolina", lat: 35.9940, lng: -78.8986 },
  { name: "Greensboro",     county: "Guilford",            state: "NC", region: "North Carolina", lat: 36.0726, lng: -79.7920 },
  { name: "Winston-Salem",  county: "Forsyth",             state: "NC", region: "North Carolina", lat: 36.0999, lng: -80.2442 },
  { name: "Asheville",      county: "Buncombe",            state: "NC", region: "North Carolina", lat: 35.5951, lng: -82.5515 },
  { name: "Wilmington",     county: "New Hanover",         state: "NC", region: "North Carolina", lat: 34.2257, lng: -77.9447 },
  { name: "Fayetteville",   county: "Cumberland",          state: "NC", region: "North Carolina", lat: 35.0527, lng: -78.8784 },
  // South Carolina
  { name: "Columbia",       county: "Richland",            state: "SC", region: "South Carolina", lat: 34.0007, lng: -81.0348 },
  { name: "Charleston",     county: "Charleston",          state: "SC", region: "South Carolina", lat: 32.7765, lng: -79.9311 },
  { name: "Greenville",     county: "Greenville",          state: "SC", region: "South Carolina", lat: 34.8526, lng: -82.3940 },
  { name: "Myrtle Beach",   county: "Horry",               state: "SC", region: "South Carolina", lat: 33.6891, lng: -78.8867 },
  { name: "Spartanburg",    county: "Spartanburg",         state: "SC", region: "South Carolina", lat: 34.9496, lng: -81.9320 },
  // Georgia
  { name: "Atlanta",        county: "Fulton",              state: "GA", region: "Georgia",        lat: 33.7490, lng: -84.3880 },
  { name: "Savannah",       county: "Chatham",             state: "GA", region: "Georgia",        lat: 32.0809, lng: -81.0912 },
  { name: "Augusta",        county: "Richmond",            state: "GA", region: "Georgia",        lat: 33.4735, lng: -82.0105 },
  { name: "Athens",         county: "Clarke",              state: "GA", region: "Georgia",        lat: 33.9519, lng: -83.3576 },
  { name: "Columbus",       county: "Muscogee",            state: "GA", region: "Georgia",        lat: 32.4610, lng: -84.9877 },
  { name: "Macon",          county: "Bibb",                state: "GA", region: "Georgia",        lat: 32.8407, lng: -83.6324 },
  // Tennessee
  { name: "Nashville",      county: "Davidson",            state: "TN", region: "Tennessee",      lat: 36.1627, lng: -86.7816 },
  { name: "Memphis",        county: "Shelby",              state: "TN", region: "Tennessee",      lat: 35.1495, lng: -90.0490 },
  { name: "Knoxville",      county: "Knox",                state: "TN", region: "Tennessee",      lat: 35.9606, lng: -83.9207 },
  { name: "Chattanooga",    county: "Hamilton",            state: "TN", region: "Tennessee",      lat: 35.0456, lng: -85.3097 },
  // Virginia
  { name: "Richmond",       county: "Richmond City",       state: "VA", region: "Virginia",       lat: 37.5407, lng: -77.4360 },
  { name: "Virginia Beach", county: "Virginia Beach City", state: "VA", region: "Virginia",       lat: 36.8529, lng: -75.9780 },
  { name: "Norfolk",        county: "Norfolk City",        state: "VA", region: "Virginia",       lat: 36.8508, lng: -76.2859 },
  { name: "Roanoke",        county: "Roanoke City",        state: "VA", region: "Virginia",       lat: 37.2710, lng: -79.9414 },
  { name: "Charlottesville",county: "Charlottesville City",state: "VA", region: "Virginia",       lat: 38.0293, lng: -78.4767 },
];

// Great-circle miles between two lat/lng points (Haversine).
function milesBetween(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  const a = s1 * s1 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * s2 * s2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Parse "25 Miles" / "50 mi" / "100" → number (miles) for coverage rings.
function parseMiles(raw) {
  if (!raw) return 0;
  const m = String(raw).match(/\d+/);
  return m ? parseInt(m[0], 10) : 0;
}

function daysBetween(iso, now) {
  if (!iso) return Infinity;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return Infinity;
  return (now - then) / (1000 * 60 * 60 * 24);
}

// Tiny deterministic jitter so co-located applicants don't stack.
function jitter(v, seed) {
  const rnd = Math.sin(seed * 12.9898) * 43758.5453;
  const frac = rnd - Math.floor(rnd);
  return v + (frac - 0.5) * 0.06;
}

function aggregateCities(list) {
  // Bucket by city name (`resolved_city` → `desired_location` → `region`) so
  // two applicants living in the same city but at different ZIPs — each with
  // its own centroid — roll up into ONE entry in the Heaviest Markets list.
  // Previously we keyed on `lat.toFixed(3),lng.toFixed(3)` which produced
  // duplicate "Charlotte, NC" rows whenever the same city had applicants
  // across multiple ZIPs. Only fall back to the coordinate key when no
  // human-readable label is available (bad geocode data) so those points
  // don't collide with unrelated cities.
  const map = new Map();
  for (const a of list) {
    if (a.latitude == null || a.longitude == null) continue;
    const label = a.resolved_city || a.desired_location || a.region;
    const key = label
      ? `city::${label.trim().toLowerCase()}`
      : `coord::${a.latitude.toFixed(3)},${a.longitude.toFixed(3)}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        city: label || a.region,
        region: a.region, lat: a.latitude, lng: a.longitude,
        count: 0, avgScore: 0, totalScore: 0, applicants: [],
      });
    }
    const bucket = map.get(key);
    bucket.count += 1;
    bucket.totalScore += (a.score || 0);
    bucket.avgScore = bucket.totalScore / bucket.count;
    bucket.applicants.push(a);
  }
  return [...map.values()].sort((x, y) => y.count - x.count);
}

function esc(s) {
  return String(s || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

// Motion presets — one source of truth so the whole page reveals in sync.
const STAGGER_CONTAINER = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
};
const FADE_UP = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
};

export default function ApplicantHeatmap({
  applicants,
  activeStages,
  height = "620px",
  entity = "applicant",
}) {
  // Copy helper — one prop drives every user-facing string in the heatmap
  // so `ApplicantHeatmap` can double as the rostered-photographer map with
  // the correct microcopy ("photographer" / "PHOTOGRAPHERS") throughout.
  const ent = useMemo(() => ({
    s:  entity,
    p:  entity + "s",
    S:  entity[0].toUpperCase() + entity.slice(1),
    U:  entity.toUpperCase(),
    UP: (entity + "s").toUpperCase(),
  }), [entity]);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const tileLayer = useRef(null);
  const heatLayer = useRef(null);
  const markersLayer = useRef(null);
  const ringsLayer = useRef(null);
  const pulseLayer = useRef(null);

  const [basemap, setBasemap] = useState("light");
  const [mode, setMode] = useState("density");   // "density" | "quality" | "coverage"
  const [minScore, setMinScore] = useState(0);
  const [showRings, setShowRings] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Live zoom level → drives grouping vs per-city detail rendering.
  const [zoomLevel, setZoomLevel] = useState(REGION_VIEWS.Southeast.zoom);
  // State-code filter — click NC/SC/GA/TN/VA to isolate that region;
  // click the active one again (or ALL) to clear.
  const [stateFilter, setStateFilter] = useState(null);
  // Applicant surfaced from a map-popup "View details" click.
  const [selectedApplicant, setSelectedApplicant] = useState(null);

  const nowRef = useRef(Date.now());
  // Keep a live pointer to the latest applicants list so the leaflet
  // popup click handler (attached inside effects) can look up the full
  // record without going stale between renders.
  const applicantsRef = useRef(applicants);
  useEffect(() => { applicantsRef.current = applicants; }, [applicants]);

  const filtered = useMemo(() => {
    return applicants.filter(
      (a) =>
        activeStages.has(a.stage) &&
        (a.score || 0) >= minScore &&
        a.latitude != null &&
        a.longitude != null &&
        (!stateFilter || a.region === stateFilter),
    );
  }, [applicants, activeStages, minScore, stateFilter]);

  const cities = useMemo(() => aggregateCities(filtered), [filtered]);

  // Zoom-aware grouping. Far out → coarse grid clusters (color pips only,
  // non-interactive). Close in → per-city markers with numbers + popups.
  const clustered = useMemo(() => {
    if (zoomLevel >= HIGH_DETAIL_ZOOM) return { mode: "city", items: cities };
    const grid = zoomLevel <= 5 ? 2.0 : zoomLevel <= 6 ? 1.2 : 0.8;
    const buckets = new Map();
    for (const c of cities) {
      const gy = Math.round(c.lat / grid) * grid;
      const gx = Math.round(c.lng / grid) * grid;
      const key = `${gy.toFixed(2)},${gx.toFixed(2)}`;
      let b = buckets.get(key);
      if (!b) {
        b = { key, lat: 0, lng: 0, count: 0, totalScore: 0 };
        buckets.set(key, b);
      }
      b.count += c.count;
      b.totalScore += (c.avgScore || 0) * c.count;
      b.lat += c.lat * c.count;
      b.lng += c.lng * c.count;
    }
    const items = [...buckets.values()].map((b) => ({
      ...b,
      lat: b.lat / b.count,
      lng: b.lng / b.count,
      avgScore: b.count ? b.totalScore / b.count : 0,
    }));
    return { mode: "cluster", items };
  }, [cities, zoomLevel]);

  const missingCoords = useMemo(
    () => applicants.filter((a) => a.latitude == null || a.longitude == null),
    [applicants],
  );

  const kpis = useMemo(() => {
    const total = filtered.length;
    const now = nowRef.current;
    const thisWeek = filtered.filter((a) => daysBetween(a.created_at, now) <= 7).length;
    const thisMonth = filtered.filter((a) => daysBetween(a.created_at, now) <= 30).length;
    const scored = filtered.filter((a) => (a.score || 0) > 0);
    const avgScore = scored.length ? (scored.reduce((s, a) => s + a.score, 0) / scored.length) : 0;
    const coveredRegions = new Set(filtered.map((a) => a.region));
    const coveragePct = Math.round((coveredRegions.size / REGIONS.length) * 100);
    return { total, thisWeek, thisMonth, avgScore, coveragePct };
  }, [filtered]);

  const regionStats = useMemo(() => {
    const now = nowRef.current;
    return REGIONS.map((r) => {
      const inRegion = filtered.filter((a) => a.region === r);
      const daysSince = inRegion.length
        ? Math.min(...inRegion.map((a) => daysBetween(a.created_at, now)))
        : Infinity;
      return {
        region: r,
        count: inRegion.length,
        daysSince: daysSince === Infinity ? null : Math.floor(daysSince),
      };
    });
  }, [filtered]);

  // Trending regions — used ONLY on the photographer/roster view. Surfaces
  // markets where the roster is *growing* (recent adds inside the last 30d)
  // that are NOT already our heaviest markets. This is where we should keep
  // pushing on recruiting: we already have some traction, but not yet enough
  // scale to consider it "done". Top-3 heaviest regions are excluded so we
  // never label a mature market as "trending" — that's just business as usual.
  //   count30d   — photographers added in the last 30 days
  //   count90d   — added in the last 90 days (used to compute velocity)
  //   growthPct  — count30d / count90d, higher = accelerating
  // Sort: prefer highest 30d count, then highest growthPct as a tiebreaker.
  const trendingRegions = useMemo(() => {
    const now = nowRef.current;
    const withCounts = REGIONS.map((r) => {
      const inRegion = filtered.filter((a) => a.region === r);
      const count30d = inRegion.filter((a) => daysBetween(a.created_at, now) <= 30).length;
      const count90d = inRegion.filter((a) => daysBetween(a.created_at, now) <= 90).length;
      const growthPct = count90d > 0 ? count30d / count90d : 0;
      return { region: r, count: inRegion.length, count30d, count90d, growthPct };
    });
    // Skip our 3 heaviest markets — those are established, not "trending".
    const heaviestRegions = new Set(
      [...withCounts].sort((a, b) => b.count - a.count).slice(0, 3).map((r) => r.region)
    );
    return withCounts
      .filter((r) => !heaviestRegions.has(r.region) && r.count30d > 0)
      .sort((a, b) => (b.count30d - a.count30d) || (b.growthPct - a.growthPct))
      .slice(0, 5);
  }, [filtered]);

  // Local (metro-level) coverage gaps ranked by recruiting priority.
  //   P1 · GROW HERE — has some local coverage already (1-24 applicants
  //                    inside 40 mi) AND is near other coverage (≤ 80 mi).
  //                    Highest ROI: deepen an active corridor we can build.
  //   P2 · FILL HERE — empty / very sparse (< 10 inside 40 mi) but still
  //                    close (≤ 80 mi) to an existing applicant. These are
  //                    the natural edges of our current coverage groups.
  //   P3 · FRONTIER  — everything else (> 80 mi from any coverage). Long
  //                    horizon expansion once the corridors are healthy.
  const metroGaps = useMemo(() => {
    const GAP_RADIUS_MI = 40;
    const GAP_THRESHOLD = 25;
    const CLOSE_MAX = 80;
    const rows = SE_METROS.map((m) => {
      let within = 0;
      let nearest = Infinity;
      for (const a of filtered) {
        const d = milesBetween(m.lat, m.lng, a.latitude, a.longitude);
        if (d < nearest) nearest = d;
        if (d <= GAP_RADIUS_MI) within += 1;
      }
      const nearestMi = nearest === Infinity ? null : Math.round(nearest);
      const close = nearestMi != null && nearestMi <= CLOSE_MAX;
      let priority = 3;
      let priorityLabel = "FRONTIER";
      if (close && within >= 1) {
        priority = 1;
        priorityLabel = "GROW HERE";
      } else if (close) {
        priority = 2;
        priorityLabel = "FILL HERE";
      }
      return {
        ...m,
        within,
        tier: tierFor(within) || { label: "EMPTY", tag: "0", fill: "#9CA3AF", glow: "rgba(156,163,175,0.50)" },
        nearest: nearestMi,
        priority,
        priorityLabel,
      };
    }).filter((m) => m.within < GAP_THRESHOLD);
    rows.sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      // Within P1, deeper existing corridor (more within) first — build on
      // the strongest partial coverage. Within P2/P3, emptier first.
      if (a.priority === 1) {
        if (a.within !== b.within) return b.within - a.within;
      } else {
        if (a.within !== b.within) return a.within - b.within;
      }
      return (a.nearest ?? Infinity) - (b.nearest ?? Infinity);
    });
    return rows;
  }, [filtered]);

  // Live 7-day pulse feed for the sidebar — sorted newest first, capped at 6.
  const recentPulse = useMemo(() => {
    const now = nowRef.current;
    return filtered
      .filter((a) => daysBetween(a.created_at, now) <= 7)
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
      .slice(0, 6)
      .map((a) => ({
        ...a,
        daysAgo: Math.max(0, Math.floor(daysBetween(a.created_at, now))),
      }));
  }, [filtered]);

  // --- Map mount (once) ---
  useEffect(() => {
    if (mapInstance.current || !mapRef.current) return;
    const map = L.map(mapRef.current, {
      center: REGION_VIEWS.Southeast.center,
      zoom: REGION_VIEWS.Southeast.zoom,
      minZoom: 4, maxZoom: 14,
      scrollWheelZoom: true, zoomControl: true, attributionControl: false,
    });
    tileLayer.current = L.tileLayer(BASEMAPS[basemap].url, {
      subdomains: "abcd", maxZoom: 19,
    }).addTo(map);
    L.control.attribution({ prefix: false })
      .addAttribution(BASEMAPS[basemap].attribution).addTo(map);
    markersLayer.current = L.layerGroup().addTo(map);
    ringsLayer.current = L.layerGroup().addTo(map);
    pulseLayer.current = L.layerGroup().addTo(map);
    mapInstance.current = map;
    // Track zoom for grouping/detail transitions.
    const onZoom = () => setZoomLevel(map.getZoom());
    map.on("zoomend", onZoom);
    return () => { map.off("zoomend", onZoom); map.remove(); mapInstance.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Basemap swap ---
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || !tileLayer.current) return;
    map.removeLayer(tileLayer.current);
    tileLayer.current = L.tileLayer(BASEMAPS[basemap].url, {
      subdomains: "abcd", maxZoom: 19,
    }).addTo(map);
  }, [basemap]);

  // --- Redraw heat + markers + rings + pulses ---
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    // Zoomed-out (cluster mode): NO heat layer — leaflet.heat's Gaussian
    // aggregation invents colors that don't match the tier scale. We render
    // per-city glows instead (see the cluster branch below). Zoomed-in
    // (city mode): heat layer with per-applicant jittered points.
    const detail = clustered.mode === "city";
    if (heatLayer.current) { map.removeLayer(heatLayer.current); heatLayer.current = null; }
    if (detail) {
      const points = filtered.map((a) => {
        const seed = a.id.charCodeAt(0) + a.id.charCodeAt(a.id.length - 1);
        const weight = mode === "quality" ? Math.max(0.2, (a.score || 1) / 2) : 1;
        return [jitter(a.latitude, seed), jitter(a.longitude, seed + 1), weight];
      });
      heatLayer.current = L.heatLayer(points, {
        radius: 52,
        blur: 22,
        maxZoom: 11,
        max: 100,
        minOpacity: 0.42,
        gradient: {
          0.00: "rgba(0,0,0,0)",
          0.03: "rgba(107,114,128,0.85)",
          0.10: "#15803D",
          0.25: "#CA8A04",
          0.50: "#DC2626",
          1.00: "#7F1D1D",
        },
      }).addTo(map);
    }

    ringsLayer.current.clearLayers();
    if (showRings) {
      for (const a of filtered) {
        const miles = parseMiles(a.desired_travel_distance);
        if (!miles) continue;
        L.circle([a.latitude, a.longitude], {
          radius: miles * MILES_TO_METERS,
          color: "#6B0F1A", weight: 1, opacity: 0.35,
          fillColor: "#6B0F1A", fillOpacity: 0.05,
          interactive: false,
        }).addTo(ringsLayer.current);
      }
    }

    markersLayer.current.clearLayers();
    const isDark = basemap === "dark";

    // ─── Zoomed-out: paint the coverage map — behaviour switches on the
    // SIGNAL mode. DENSITY & QUALITY draw feathered per-city tier glows.
    // COVERAGE draws every photographer's actual ZIP + travel-radius as a
    // soft geographic disc, colored by their metro's tier, so overlapping
    // service areas naturally deepen and gaps read as pale.
    if (clustered.mode === "cluster") {
      if (mode === "coverage") {
        // Build a per-applicant → city-count lookup once so tier lookup
        // stays O(1) inside the loop.
        const countByAppId = new Map();
        for (const cty of cities) {
          for (const ap of cty.applicants || []) countByAppId.set(ap.id, cty.count);
        }
        for (const a of filtered) {
          const cnt = countByAppId.get(a.id) || 1;
          const t = tierFor(cnt);
          if (!t) continue;
          const miles = parseMiles(a.desired_travel_distance) || 25;
          L.circle([a.latitude, a.longitude], {
            radius: miles * MILES_TO_METERS,
            fillColor: t.fill,
            fillOpacity: 0.20,
            color: t.fill,
            weight: 1,
            opacity: 0.35,
            interactive: false,
            className: "reelty-coverage-disc",
          }).addTo(markersLayer.current);
        }
        pulseLayer.current.clearLayers();
        return;
      }

      // DENSITY / QUALITY — feathered per-city tier glows.
      for (const c of clustered.items) {
        const t = tierFor(c.count);
        if (!t) continue;
        // Log-scaled radius so a lone applicant stays a whisper and a
        // saturated metro really blooms.
        const r = Math.min(150, 32 + Math.log(c.count + 1) * 28);
        const size = r * 2;
        const icon = L.divIcon({
          className: "reelty-tier-glow",
          html: `<div class="reelty-tier-glow-inner"
                      style="width:${size}px;height:${size}px;
                             background:radial-gradient(circle,
                               ${t.fill}B3 0%,
                               ${t.fill}80 18%,
                               ${t.fill}4D 40%,
                               ${t.fill}1F 65%,
                               ${t.fill}00 100%);"></div>`,
          iconSize: [size, size],
          iconAnchor: [r, r],
        });
        L.marker([c.lat, c.lng], {
          icon,
          interactive: false,
          keyboard: false,
        }).addTo(markersLayer.current);
      }
      pulseLayer.current.clearLayers();
      return;
    }

    // ─── Zoomed-in: per-city markers with numbers, tooltips, popups.
    // Track which city keys have a "recent" applicant (last 7 days) so we
    // can fold the pulse ring INTO the main marker instead of stacking a
    // separate dot on top (that was the source of the overlap mess).
    const now = nowRef.current;
    const recentKeys = new Set();
    for (const a of filtered) {
      if (daysBetween(a.created_at, now) <= 7) {
        recentKeys.add(`${a.latitude.toFixed(3)},${a.longitude.toFixed(3)}`);
      }
    }

    for (const c of cities) {
      const r = Math.min(30, 11 + Math.sqrt(c.count) * 6);
      const size = r * 2;
      const fontSize = Math.max(11, Math.min(20, r * 0.65));
      const isRecent = recentKeys.has(c.key);
      const fill = isDark ? "#1A0F0A" : "#F5EBD9";
      const border = isDark ? "#B08D57" : "#6B0F1A";
      const textColor = isDark ? "#F5EBD9" : "#6B0F1A";

      // Single beautiful marker — circle background, number in the center,
      // optional gold pulsing ring for recent-week cities. Everything is
      // one DOM element so it never overlaps with a companion label layer.
      const icon = L.divIcon({
        className: "reelty-city-marker",
        html: `
          <div class="reelty-city-marker-inner ${isRecent ? "is-recent" : ""}"
               style="
                 width:${size}px;height:${size}px;
                 background:${fill};
                 color:${textColor};
                 border:2px solid ${border};
                 font-size:${fontSize}px;
               ">
            <span class="reelty-city-marker-count">${c.count}</span>
          </div>
        `,
        iconSize: [size, size],
        iconAnchor: [r, r],
      });
      const marker = L.marker([c.lat, c.lng], { icon, riseOnHover: true });

      // Hover reveals the city name as a permanent tooltip — replaces the
      // "hover mystery" of the old text-free circle.
      marker.bindTooltip(
        `<span style="font-family:'Noir Pro',sans-serif;font-weight:700;font-size:13px">${esc(c.city)}</span>`
         + `<span style="opacity:0.7;font-family:'Noir Pro',sans-serif;font-weight:500;font-size:11px;letter-spacing:0.02em;margin-left:6px">${c.count} · ${c.avgScore.toFixed(1)}★</span>`,
        {
          direction: "top",
          offset: [0, -r + 4],
          className: `reelty-tooltip${isDark ? " reelty-tooltip-dark" : ""}`,
          opacity: 1,
        },
      );

      const list = c.applicants.slice(0, 6).map((a) => {
        const stars = "★".repeat(a.score || 0) + "☆".repeat(5 - (a.score || 0));
        // Small inline role tag so a mixed Charlotte popup shows Photog vs
        // Pilot rows without needing to open the detail modal.
        const role = a.applied_role === "pilot" ? "pilot" : "photographer";
        const roleColor = role === "pilot" ? "#3F7A2E" : "#6B0F1A";
        const roleBg = role === "pilot" ? "rgba(63,122,46,0.14)" : "rgba(107,15,26,0.12)";
        const roleLabel = role === "pilot" ? "PILOT" : "PHOTOG";
        return `<button type="button"
          data-applicant-id="${esc(a.id)}"
          class="reelty-popup-applicant"
          style="width:100%;text-align:left;background:transparent;border:none;padding:8px 8px;margin-top:4px;border-radius:4px;cursor:pointer;transition:background .15s ease;display:block;color:inherit;font-family:inherit"
        >
          <div style="display:flex;justify-content:space-between;gap:8px;font-size:11px;align-items:center">
            <span style="display:inline-flex;align-items:center;gap:6px;min-width:0">
              <span style="color:${isDark ? "#F5EBD9" : "#1A0F0A"};font-weight:600">${esc(a.name)}</span>
              <span style="font-family:'Noir Pro',sans-serif;font-size:8.5px;font-weight:700;letter-spacing:0.14em;color:${roleColor};background:${roleBg};border:1px solid ${roleColor}55;border-radius:2px;padding:1px 5px;line-height:1.4">${roleLabel}</span>
            </span>
            <span style="color:#B08D57;font-size:9px;letter-spacing:0.05em">${stars}</span>
          </div>
          <div style="display:flex;justify-content:space-between;gap:8px;font-size:10px;color:${isDark ? "#B08D57" : "#78584B"};letter-spacing:0.05em;margin-top:2px">
            <span>${esc(a.stage)}</span>
            <span style="font-family:'Noir Pro',sans-serif;font-weight:700;letter-spacing:0.1em;text-transform:uppercase">
              View →
            </span>
          </div>
        </button>`;
      }).join("");
      const more = c.applicants.length > 6
        ? `<div style="opacity:0.65;margin-top:6px;font-size:10px">+ ${c.applicants.length - 6} more</div>` : "";
      marker.bindPopup(
        `<div style="min-width:240px;font-family:'Noir Pro',sans-serif">
          <div style="font-family:'Noir Pro',sans-serif;font-size:17px;font-weight:700;color:${isDark ? "#F5EBD9" : "#1A0F0A"};line-height:1.15;letter-spacing:-0.005em">${esc(c.city)}</div>
          <div style="font-size:11px;color:${isDark ? "#B08D57" : "#78584B"};letter-spacing:0.02em;margin-top:3px;font-family:'Noir Pro',sans-serif;font-weight:500">${esc(c.region)} · ${c.count} ${ent.U}${c.count === 1 ? "" : "S"} · AVG ${c.avgScore.toFixed(1)}★</div>
          <div style="margin-top:10px;padding-top:8px;border-top:1px solid ${isDark ? "#F5EBD9" : "#1A0F0A"}22;max-width:280px">${list}${more}</div>
        </div>`,
        { className: `reelty-popup${isDark ? " reelty-popup-dark" : ""}`, maxWidth: 320 },
      );
      // Wire the popup's "View details" buttons up to the React modal
      // whenever the popup opens. Handlers are attached once per open.
      marker.on("popupopen", (evt) => {
        const el = evt.popup.getElement();
        if (!el) return;
        const buttons = el.querySelectorAll("[data-applicant-id]");
        buttons.forEach((btn) => {
          btn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            const id = btn.getAttribute("data-applicant-id");
            const app = applicantsRef.current.find((x) => x.id === id);
            if (app) setSelectedApplicant(app);
          });
        });
      });

      markersLayer.current.addLayer(marker);
    }

    // Pulse layer is now baked into the city marker via `.is-recent` — keep
    // the layer group around (mount code adds it) but always cleared so no
    // stacking artifacts survive re-renders.
    pulseLayer.current.clearLayers();
  }, [filtered, cities, clustered, mode, showRings, basemap]);

  const flyTo = useCallback((view) => {
    const map = mapInstance.current;
    if (!map) return;
    map.flyTo(view.center, view.zoom, { duration: 0.8, easeLinearity: 0.25 });
  }, []);

  const searchAndFly = useCallback(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return;
    const hit = cities.find((c) => c.city.toLowerCase().includes(q));
    if (hit) {
      flyTo({ center: [hit.lat, hit.lng], zoom: 10 });
      toast.success(`Zoomed to ${hit.city} — ${hit.count} applicant${hit.count === 1 ? "" : "s"}.`);
    } else {
      toast.error(`No applicant city matches "${searchQuery}".`);
    }
  }, [searchQuery, cities, flyTo]);

  const copyTopMarkets = useCallback(() => {
    const top = cities.slice(0, 5).map((c, i) => `${i + 1}. ${c.city} — ${c.count} applicants (avg ${c.avgScore.toFixed(1)}★)`).join("\n");
    const gaps = regionStats.filter((r) => r.count < 3).map((r) => `• ${r.region}: ${r.count} applicant${r.count === 1 ? "" : "s"}`).join("\n");
    const text = `REELTY APPLICANT PIPELINE — SNAPSHOT
Total: ${kpis.total} · Coverage: ${kpis.coveragePct}% of target regions · Avg score: ${kpis.avgScore.toFixed(1)}★

TOP MARKETS:
${top || "(no data)"}

HR TARGETING OPPORTUNITIES (< 3 ${ent.p}):
${gaps || "(all regions covered)"}`;
    navigator.clipboard.writeText(text).then(
      () => toast.success("Snapshot copied to clipboard."),
      () => toast.error("Clipboard write failed."),
    );
  }, [cities, regionStats, kpis]);

  const topCities = cities.slice(0, 5);
  const maxTopCount = topCities[0]?.count || 1;
  const gaps = regionStats.filter((r) => r.count < 3);
  const maxRegionCount = Math.max(1, ...regionStats.map((r) => r.count));

  return (
    <motion.div
      variants={STAGGER_CONTAINER}
      initial="hidden"
      animate="show"
      className="space-y-5"
      data-testid="applicant-heatmap"
    >
      {/* Scoped CSS — marker + tooltip + popup + heat label */}
      <style>{`
        .reelty-city-marker {background:transparent !important;border:none !important}
        .reelty-tier-glow {background:transparent !important;border:none !important;pointer-events:none !important}
        .reelty-tier-glow-inner {
          border-radius:9999px;
          filter:blur(10px);
          will-change:transform;
        }
        .reelty-coverage-disc {filter:blur(6px);mix-blend-mode:multiply}
        .reelty-cluster-pip {background:transparent !important;border:none !important;pointer-events:none !important}
        .reelty-cluster-pip-inner {
          border-radius:9999px;
          background:radial-gradient(circle, #6B0F1A 0%, rgba(107,15,26,0.75) 55%, rgba(107,15,26,0.15) 100%);
          box-shadow:0 0 14px -2px rgba(107,15,26,0.55);
        }
        .reelty-cluster-pip-inner.is-dark {
          background:radial-gradient(circle, #B08D57 0%, rgba(176,141,87,0.7) 55%, rgba(176,141,87,0.15) 100%);
          box-shadow:0 0 16px -2px rgba(176,141,87,0.7);
        }
        .reelty-city-marker-inner {
          border-radius:9999px;
          display:flex;align-items:center;justify-content:center;
          font-family:'Noir Pro', sans-serif;
          font-weight:700;
          letter-spacing:-0.01em;
          box-shadow:0 6px 14px -6px rgba(26,15,10,0.55), 0 0 0 4px rgba(245,235,217,0.7);
          transition:transform .18s ease, box-shadow .18s ease;
          position:relative;
        }
        .reelty-city-marker:hover .reelty-city-marker-inner {
          transform:scale(1.08);
          box-shadow:0 12px 24px -8px rgba(26,15,10,0.75), 0 0 0 4px rgba(245,235,217,0.9);
          z-index:1000;
        }
        .reelty-city-marker-count {line-height:1;pointer-events:none}
        .reelty-city-marker-inner.is-recent::before {
          content:"";
          position:absolute;inset:-6px;
          border-radius:9999px;
          border:2px solid #B08D57;
          animation:reelty-marker-pulse 2.2s ease-out infinite;
          pointer-events:none;
        }
        @keyframes reelty-marker-pulse {
          0%   {transform:scale(0.7);opacity:0.9}
          70%  {transform:scale(1.35);opacity:0}
          100% {transform:scale(1.35);opacity:0}
        }
        .reelty-tooltip {
          background:linear-gradient(180deg,#FBF4E4 0%,#F5EBD9 100%) !important;
          border:1px solid rgba(26,15,10,0.15) !important;
          border-radius:4px !important;
          color:#1A0F0A !important;
          padding:6px 10px !important;
          box-shadow:0 10px 24px -12px rgba(26,15,10,0.55) !important;
          white-space:nowrap;
        }
        .reelty-tooltip::before {display:none !important}
        .reelty-tooltip-dark {
          background:linear-gradient(180deg,#1A0F0A 0%,#231510 100%) !important;
          border-color:rgba(176, 141, 87,0.35) !important;
          color:#F5EBD9 !important;
        }
        .reelty-popup .leaflet-popup-content-wrapper {
          background:linear-gradient(180deg,#FBF4E4 0%,#F5EBD9 100%);
          border-radius:4px;
          box-shadow:0 24px 48px -12px #1A0F0A66, 0 0 0 1px #1A0F0A18;
          padding:14px 16px;
        }
        .reelty-popup .leaflet-popup-content {margin:0;line-height:1.4}
        .reelty-popup .leaflet-popup-tip {background:#F5EBD9;box-shadow:0 0 0 1px #1A0F0A18}
        .reelty-popup-applicant:hover {background:rgba(107,15,26,0.06) !important}
        .reelty-popup-dark .reelty-popup-applicant:hover {background:rgba(176,141,87,0.14) !important}
        .reelty-popup-dark .leaflet-popup-content-wrapper {
          background:linear-gradient(180deg,#1A0F0A 0%,#231510 100%);
          border-color:#B08D5733;color:#F5EBD9;
          box-shadow:0 24px 48px -12px #000, 0 0 0 1px #B08D5733;
        }
        .reelty-popup-dark .leaflet-popup-tip {background:#231510;box-shadow:0 0 0 1px #B08D5733}
        .leaflet-container {font-family:inherit; background:#F5EBD9 !important;}
        .reelty-heat-frame {
          background:
            radial-gradient(ellipse at 20% 0%,#6B0F1A22 0%,transparent 55%),
            radial-gradient(ellipse at 100% 100%,#B08D571C 0%,transparent 55%),
            linear-gradient(180deg,#FBF4E4,#F5EBD9);
        }
        .reelty-heat-legend {
          background:linear-gradient(180deg,rgba(251,244,228,0.92) 0%,rgba(245,235,217,0.92) 100%);
          border:1px solid rgba(26,15,10,0.10);
          box-shadow:0 12px 28px -14px rgba(26,15,10,0.35), 0 0 0 1px rgba(176,141,87,0.12) inset;
          backdrop-filter:blur(8px);
          -webkit-backdrop-filter:blur(8px);
        }
      `}</style>

      {/* ─────────────────────────────────────────────────────────────────
          HERO BAND — editorial oxblood/gold showcase for the total lead
          count. Big DM-serif number, supporting prose, and the KPI band.
         ────────────────────────────────────────────────────────────── */}
      <motion.section variants={FADE_UP} className="relative overflow-hidden rounded-sm border border-[#1A0F0A]/12 bg-gradient-to-br from-[#FBF4E4] via-[#F5EBD9] to-[#F0E1C4] px-5 py-6 sm:px-7 sm:py-8">
        {/* Decorative floating orbs */}
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 w-72 h-72 rounded-full bg-[#6B0F1A]/8 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -left-16 bottom-0 w-56 h-56 rounded-full bg-[#B08D57]/10 blur-3xl" />

        <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-mono-tag text-[10px] tracking-[0.2em] text-[#6B0F1A]/90">
              <span className="w-6 h-px bg-[#6B0F1A]/60" />
              {ent.U} DENSITY · GEOGRAPHIC INTELLIGENCE
            </div>
            <div className="mt-3 flex items-baseline gap-3 flex-wrap">
              <span className="font-display text-6xl sm:text-7xl font-extrabold tracking-tighter text-[#1A0F0A] leading-none">
                {kpis.total}
              </span>
              <span className="font-display text-xl sm:text-2xl text-[#4D3F35] leading-tight max-w-md">
                {kpis.total === 0
                  ? `No ${ent.p} on the map yet.`
                  : kpis.total === 1
                  ? <>{ent.s} pinned across the Southeast, awaiting your outreach.</>
                  : <>{ent.p} pinned across <b className="text-[#1A0F0A]">{new Set(filtered.map(a=>a.region)).size} of {REGIONS.length}</b> target regions.</>
                }
              </span>
            </div>
          </div>

          {/* KPI chips — inline, dividers between */}
          <div
            className="flex items-stretch gap-0 divide-x divide-[#1A0F0A]/12 rounded-sm bg-[#FBF4E4]/70 backdrop-blur-sm border border-[#1A0F0A]/10 overflow-x-auto"
            data-testid="heatmap-kpis"
          >
            <KpiChip icon={Users}     label="TOTAL"           value={kpis.total}                        testId="kpi-total" />
            <KpiChip icon={Zap}       label="LAST 7 DAYS"     value={kpis.thisWeek}    accent="#B08D57" testId="kpi-week" />
            <KpiChip icon={Activity}  label="LAST 30 DAYS"    value={kpis.thisMonth}                    testId="kpi-month" />
            <KpiChip icon={Sparkles}  label="AVG SCORE"       value={`${kpis.avgScore.toFixed(1)}★`}   testId="kpi-avg-score" />
            <KpiChip icon={Target}    label="COVERAGE"        value={`${kpis.coveragePct}%`} accent="#6B0F1A" testId="kpi-coverage" />
          </div>
        </div>
      </motion.section>

      {/* ─────────────────────────────────────────────────────────────────
          MISSING COORDS — editorial nudge only when someone can't be placed.
         ────────────────────────────────────────────────────────────── */}
      {missingCoords.length > 0 && (
        <motion.div
          variants={FADE_UP}
          className="flex items-start gap-3 rounded-sm border border-[#B08D57]/40 bg-[#F5EBD9] px-4 py-3 shadow-[inset_3px_0_0_#B08D57]"
          data-testid="heatmap-missing-coords"
        >
          <MapPin className="w-4 h-4 text-[#B08D57] mt-0.5 shrink-0" strokeWidth={2} />
          <div className="text-xs text-[#4D3F35] leading-relaxed">
            <b className="text-[#1A0F0A]">
              {missingCoords.length} {ent.s}{missingCoords.length === 1 ? " is" : "s are"} off the map
            </b>{" "}
            — no valid US ZIP. The heatmap is strictly ZIP-driven; add a ZIP from the Pipeline view to place{" "}
            {missingCoords.length === 1 ? "them" : "each of them"} at the ZIP's real city.
          </div>
        </motion.div>
      )}

      {/* ─────────────────────────────────────────────────────────────────
          TOOLBAR — grouped semantic clusters, refined spacing.
         ────────────────────────────────────────────────────────────── */}
      <motion.section
        variants={FADE_UP}
        className="rounded-sm border border-[#1A0F0A]/12 bg-[#FBF4E4] px-3 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2"
        data-testid="heatmap-toolbar"
      >
        {/* GROUP 1: view */}
        <ToolGroup label="VIEW" icon={Layers}>
          <SegBar>
            {Object.entries(BASEMAPS).map(([k, v]) => {
              const Icon = v.icon;
              return (
                <SegBtn key={k} active={basemap === k} onClick={() => setBasemap(k)} testId={`basemap-${k}`} title={v.label}>
                  <Icon className="w-3 h-3" strokeWidth={2} /> {v.label.toUpperCase()}
                </SegBtn>
              );
            })}
          </SegBar>
        </ToolGroup>

        {/* GROUP 2: signal mode */}
        <ToolGroup label="SIGNAL" icon={Radar}>
          <SegBar>
            <SegBtn active={mode === "density"} onClick={() => setMode("density")} testId="heatmap-mode-density">DENSITY</SegBtn>
            <SegBtn active={mode === "quality"} onClick={() => setMode("quality")} testId="heatmap-mode-quality">QUALITY</SegBtn>
            <SegBtn active={mode === "coverage"} onClick={() => setMode("coverage")} testId="heatmap-mode-coverage" title="ZIP + travel-radius reach">COVERAGE</SegBtn>
          </SegBar>
        </ToolGroup>

        {/* GROUP 3: quality gate */}
        <ToolGroup label="MIN SCORE" icon={Sparkles}>
          <div className="inline-flex gap-1">
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setMinScore(n)}
                data-testid={`min-score-${n}`}
                className={`w-7 h-7 rounded-sm border text-[10px] font-bold transition-colors ${
                  minScore === n
                    ? "bg-[#6B0F1A] text-[#F5EBD9] border-[#6B0F1A] shadow-inner"
                    : "bg-transparent text-[#78584B] border-[#1A0F0A]/15 hover:bg-[#1A0F0A]/5"
                }`}
              >
                {n === 0 ? "0" : `${n}★`}
              </button>
            ))}
          </div>
        </ToolGroup>

        {/* GROUP 4: rings */}
        <button
          type="button"
          onClick={() => setShowRings((r) => !r)}
          data-testid="toggle-rings"
          className={`px-3 h-8 inline-flex items-center gap-1.5 text-[10px] font-mono-tag tracking-wide rounded-sm border transition-colors ${
            showRings
              ? "bg-[#6B0F1A] text-[#F5EBD9] border-[#6B0F1A]"
              : "bg-transparent text-[#4D3F35] border-[#1A0F0A]/15 hover:bg-[#1A0F0A]/5"
          }`}
        >
          <Compass className="w-3 h-3" strokeWidth={2} /> TRAVEL RINGS
        </button>

        {/* GROUP 5: state filter + zoom */}
        <ToolGroup label="STATE" icon={Target}>
          <div className="inline-flex items-center gap-1 flex-wrap">
            <button
              type="button"
              onClick={() => { setStateFilter(null); flyTo(REGION_VIEWS.Southeast); }}
              data-testid="zoom-southeast"
              title="Show every state"
              className={`px-2 h-7 text-[10px] font-mono-tag tracking-wider rounded-sm border transition-colors ${
                stateFilter === null
                  ? "bg-[#6B0F1A] border-[#6B0F1A] text-[#F5EBD9]"
                  : "border-[#1A0F0A]/15 text-[#4D3F35] hover:bg-[#1A0F0A]/5"
              }`}
            >ALL</button>
            {REGIONS.map((r) => {
              const code = STATE_CODES[r];
              const isActive = stateFilter === r;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => {
                    const next = isActive ? null : r;
                    setStateFilter(next);
                    flyTo(next ? REGION_VIEWS[r] : REGION_VIEWS.Southeast);
                  }}
                  data-testid={`zoom-${r.replace(/ /g, "-").toLowerCase()}`}
                  title={`${r} — click to filter to ${code} only`}
                  className={`px-2 h-7 text-[10px] font-mono-tag tracking-wider rounded-sm border transition-colors ${
                    isActive
                      ? "bg-[#6B0F1A] border-[#6B0F1A] text-[#F5EBD9]"
                      : "border-[#1A0F0A]/15 text-[#4D3F35] hover:bg-[#1A0F0A]/5"
                  }`}
                >{code}</button>
              );
            })}
          </div>
        </ToolGroup>

        {/* Search + snapshot pushed to end */}
        <div className="ml-auto flex items-center gap-2 flex-1 min-w-[220px]">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 text-[#78584B] absolute left-2.5 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchAndFly(); } }}
              placeholder="Fly to city…"
              data-testid="heatmap-search"
              className="w-full pl-8 pr-2 h-8 bg-white/60 border border-[#1A0F0A]/15 focus:outline-none focus:border-[#6B0F1A] rounded-sm text-xs text-[#1A0F0A] placeholder:text-[#78584B]/70"
            />
          </div>
          <button
            type="button"
            onClick={copyTopMarkets}
            data-testid="copy-snapshot"
            title="Copy a text snapshot of the top markets + coverage gaps for HR handoff."
            className="px-2.5 h-8 inline-flex items-center gap-1 text-[10px] font-mono-tag tracking-wide rounded-sm border border-[#1A0F0A]/15 text-[#4D3F35] hover:bg-[#1A0F0A]/5 whitespace-nowrap"
          >
            <Copy className="w-3 h-3" strokeWidth={2} /> COPY SNAPSHOT
          </button>
        </div>
      </motion.section>

      {/* ─────────────────────────────────────────────────────────────────
          HERO MAP + EDITORIAL SIDEBAR
         ────────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-5 items-start">
        {/* MAP — presented as a hero object with elegant frame + shadow */}
        <motion.div variants={FADE_UP} className="space-y-2">
          <div className="reelty-heat-frame relative rounded-sm border border-[#1A0F0A]/12 overflow-hidden shadow-[0_16px_48px_-24px_rgba(26,15,10,0.35)]">
            {/* corner ornament */}
            <div aria-hidden className="pointer-events-none absolute top-0 left-0 w-16 h-16 border-t border-l border-[#6B0F1A]/25 z-[400]" />
            <div aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-16 h-16 border-b border-r border-[#B08D57]/40 z-[400]" />
            <div
              ref={mapRef}
              style={{ height, minHeight: "440px" }}
              className="relative"
            />
          </div>
          {/* COVERAGE TIER LEGEND — anchored right below the map so it
              reads together with the visualization at a glance. */}
          <div
            className="reelty-heat-legend flex flex-wrap items-center gap-x-4 gap-y-1.5 px-3 py-2 rounded-sm border border-[#1A0F0A]/10"
            data-testid="heatmap-legend"
          >
            <div className="font-mono-tag text-[9px] tracking-[0.25em] text-[#78584B]">
              {ent.UP} · 40 MI
            </div>
            {[...COVERAGE_TIERS].reverse().map((t) => (
              <div key={t.label} className="flex items-center gap-1.5">
                <span
                  aria-hidden
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ background: t.fill, boxShadow: `0 0 6px -1px ${t.glow}` }}
                />
                <span className="font-mono-tag text-[10px] tracking-wider text-[#1A0F0A]">
                  {t.tag}
                </span>
                <span className="font-mono-tag text-[9px] tracking-wider text-[#78584B]/85">
                  {t.label}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        <aside
          className="flex flex-col overflow-hidden"
          style={{ height: `calc(${height} + 46px)` }}
        >
          {/* ② COVERAGE GAPS — metro-level, tiered on the same 0-10-25-50-100
              coverage scale as the heatmap. Any metro whose 40-mile applicant
              pool sits below "decent" (< 25) surfaces here, colored by tier. */}
          <motion.div
            variants={FADE_UP}
            className="h-full flex flex-col rounded-sm border border-[#1A0F0A]/12 bg-[#FBF4E4] p-5 overflow-hidden"
            data-testid="heatmap-gaps"
          >
            <PanelHeader
              icon={TrendingDown}
              kicker="LOCAL WHITE SPACE"
              title="Metro coverage gaps"
              accent="#B08D57"
            />
            {metroGaps.length === 0 ? (
              <p className="text-xs text-[#78584B] leading-relaxed mt-4">
                <span className="inline-block w-2 h-2 rounded-full bg-[#1B5E20] mr-1.5 align-middle" />
                Every tracked metro has 25+ {ent.p} within 40 mi. Coverage is decent everywhere.
              </p>
            ) : (
              <div className="mt-3">
                <p className="text-[11px] text-[#78584B] mb-4 leading-relaxed">
                  Prioritized by what to build next — <span className="font-semibold text-[#6B0F1A]">grow</span> where
                  we already have some coverage close to more, then <span className="font-semibold text-[#B08D57]">fill</span> empty
                  metros on the edge of our footprint, then <span className="font-semibold text-[#78584B]">explore</span> the frontier.
                </p>
                <ul className="space-y-1" data-testid="metro-gaps-list">
                  {metroGaps.slice(0, 6).map((m, idx) => {
                    const key = `${m.name}-${m.state}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                    const prevPriority = idx > 0 ? metroGaps[idx - 1].priority : null;
                    const showHeader = m.priority !== prevPriority;
                    const priorityColor =
                      m.priority === 1 ? "#6B0F1A" :
                      m.priority === 2 ? "#B08D57" : "#78584B";
                    return (
                      <div key={key}>
                        {showHeader && (
                          <div
                            className="flex items-center gap-2 mt-3 mb-1.5 first:mt-0"
                            data-testid={`metro-gaps-header-p${m.priority}`}
                          >
                            <span
                              className="font-mono-tag text-[9px] tracking-[0.25em] px-1.5 py-0.5 rounded-sm"
                              style={{
                                color: priorityColor,
                                background: `${priorityColor}0F`,
                                border: `1px solid ${priorityColor}30`,
                              }}
                            >
                              P{m.priority} · {m.priorityLabel}
                            </span>
                            <span className="flex-1 h-px bg-[#1A0F0A]/8" />
                          </div>
                        )}
                        <li>
                          <button
                            type="button"
                            onClick={() => flyTo({ center: [m.lat, m.lng], zoom: 10 })}
                            data-testid={`gap-metro-${key}`}
                            className="w-full text-left group flex items-center gap-2.5 px-2 py-1.5 -mx-2 rounded-sm hover:bg-[#1A0F0A]/5 transition-colors"
                          >
                            <span
                              className="w-2 h-2 rounded-full shrink-0"
                              style={{ background: m.tier.fill, boxShadow: `0 0 8px -1px ${m.tier.glow}` }}
                              aria-hidden
                            />
                            <span className="min-w-0 flex-1">
                              <span className="flex items-baseline gap-1.5">
                                <span className="font-display text-sm font-bold text-[#1A0F0A] truncate group-hover:text-[#6B0F1A] transition-colors">
                                  {m.name}
                                </span>
                                <span className="font-mono-tag text-[9px] tracking-wider text-[#78584B]">
                                  {m.state}
                                </span>
                              </span>
                              <span className="block font-mono-tag text-[9px] tracking-wide text-[#78584B]/85 mt-0.5 truncate">
                                {m.county} County · nearest {m.nearest == null ? "—" : `${m.nearest} mi`}
                              </span>
                            </span>
                            <span
                              className="font-mono-tag text-[10px] tracking-wider shrink-0 px-1.5 py-0.5 rounded-sm"
                              style={{
                                color: m.tier.fill,
                                background: `${m.tier.fill}12`,
                                border: `1px solid ${m.tier.fill}30`,
                              }}
                            >
                              {m.within} · {m.tier.label}
                            </span>
                          </button>
                        </li>
                      </div>
                    );
                  })}
                </ul>
                {metroGaps.length > 6 && (
                  <p className="mt-3 font-mono-tag text-[10px] tracking-wider text-[#78584B]/70 shrink-0">
                    + {metroGaps.length - 6} more gap{metroGaps.length - 6 === 1 ? "" : "s"} across the Southeast
                  </p>
                )}
              </div>
            )}
          </motion.div>

        </aside>
      </div>

      {/* ─── Full-width metrics band under the map + gap panel — heaviest
          markets, recent pulse, and by-region tally live here so the map
          and the priority gap list can sit side-by-side above. ─── */}
      <motion.div variants={FADE_UP} className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* ① MARKET RANKINGS */}
          <motion.div
            variants={FADE_UP}
            className="rounded-sm border border-[#1A0F0A]/12 bg-[#FBF4E4] p-5"
            data-testid="heatmap-top-markets"
          >
            <PanelHeader
              icon={TrendingUp}
              kicker="RANKED BY VOLUME"
              title="Heaviest markets"
            />
            {topCities.length === 0 ? (
              <EmptyPanel text={`No ${ent.p} match the current filter.`} />
            ) : (
              <ul className="space-y-3 mt-4">
                {topCities.map((c, idx) => {
                  const barWidth = (c.count / maxTopCount) * 100;
                  const rank = idx + 1;
                  const t = tierFor(c.count) || COVERAGE_TIERS[COVERAGE_TIERS.length - 1];
                  return (
                    <li key={c.key}>
                      <button
                        type="button"
                        onClick={() => flyTo({ center: [c.lat, c.lng], zoom: 10 })}
                        data-testid={`top-market-${c.key}`}
                        className="w-full text-left group"
                      >
                        <div className="flex items-center gap-3">
                          <RankBadge rank={rank} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="font-display text-sm font-bold text-[#1A0F0A] truncate group-hover:text-[#6B0F1A] transition-colors">
                                {c.city}
                              </span>
                              <span
                                className="font-mono-tag text-[10px] shrink-0 tracking-wider"
                                style={{ color: t.fill }}
                              >
                                {c.count} · {t.label}
                              </span>
                            </div>
                            <div className="mt-1.5 h-1 rounded-full bg-[#1A0F0A]/8 overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${barWidth}%` }}
                                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: idx * 0.05 }}
                                className="h-full rounded-full"
                                style={{
                                  background: `linear-gradient(90deg, ${t.fill} 0%, ${t.fill}CC 100%)`,
                                  boxShadow: `0 0 8px -1px ${t.glow}`,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.div>

          {/* ③ RECENT PULSE — 7-day feed */}
          {recentPulse.length > 0 && (
            <motion.div
              variants={FADE_UP}
              className="rounded-sm border border-[#1A0F0A]/12 bg-gradient-to-br from-[#1A0F0A] to-[#231510] p-5 text-[#F5EBD9]"
              data-testid="heatmap-recent-pulse"
            >
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <div className="font-mono-tag text-[9px] tracking-[0.2em] text-[#B08D57]">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#B08D57] animate-pulse mr-1.5 align-middle" />
                    LIVE · LAST 7 DAYS
                  </div>
                  <h3 className="font-display text-base font-bold text-[#F5EBD9] mt-1">Recent pulse</h3>
                </div>
                <span className="font-display text-2xl font-extrabold text-[#B08D57] leading-none">
                  {kpis.thisWeek}
                </span>
              </div>
              <ul className="space-y-2.5">
                {recentPulse.map((a) => (
                  <li key={a.id} className="flex items-center gap-2.5 text-xs">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#B08D57] shrink-0" />
                    <span className="text-[#F5EBD9] truncate flex-1">{a.name}</span>
                    <span className="text-[#B08D57]/70 text-[10px] truncate max-w-[110px]">
                      {a.resolved_city || a.desired_location || a.region}
                    </span>
                    <span className="font-mono-tag text-[9px] tracking-wider text-[#78584B] shrink-0">
                      {a.daysAgo === 0 ? "TODAY" : `${a.daysAgo}D`}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* ④ BY REGION / TRENDING REGIONS — photographers get the growth-
              oriented "trending" variant that highlights non-top markets we
              should keep recruiting in. Applicants keep the raw region tally. */}
          {entity === "photographer" ? (
            <motion.div
              variants={FADE_UP}
              className="rounded-sm border border-[#1A0F0A]/12 bg-[#FBF4E4] p-5"
              data-testid="heatmap-trending-regions"
            >
              <PanelHeader
                kicker="LAST 30 DAYS · GROWTH"
                title="Trending regions"
              />
              <p className="text-[11px] text-[#78584B] leading-relaxed mt-2 mb-4">
                Regions outside our heaviest 3 markets where roster growth is
                picking up — keep pushing recruiting here.
              </p>
              {trendingRegions.length === 0 ? (
                <div className="rounded-sm border border-dashed border-[#1A0F0A]/15 p-4 text-center">
                  <div className="font-mono-tag text-[10px] text-[#78584B]">
                    NO NEW GROWTH THIS MONTH
                  </div>
                  <div className="text-[11px] text-[#4D3F35] mt-1.5 leading-relaxed">
                    Once photographers start joining outside your top-3 metros,
                    they'll show up here as trending.
                  </div>
                </div>
              ) : (
                <ul className="space-y-3">
                  {trendingRegions.map((r) => {
                    const pctOfTotal = r.count90d > 0 ? Math.round(r.growthPct * 100) : 0;
                    return (
                      <li key={r.region}>
                        <button
                          type="button"
                          onClick={() => flyTo(REGION_VIEWS[r.region])}
                          data-testid={`trending-region-${r.region.replace(/ /g, "-").toLowerCase()}`}
                          className="w-full text-left group rounded-sm hover:bg-[#6B0F1A]/[0.04] px-2 -mx-2 py-1.5 transition-colors"
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <TrendingUp
                                className="w-3.5 h-3.5 text-[#3F7A2E] shrink-0"
                                strokeWidth={2.25}
                              />
                              <span className="text-xs font-semibold text-[#1A0F0A] group-hover:text-[#6B0F1A] transition-colors truncate">
                                {r.region}
                              </span>
                            </div>
                            <span className="font-mono-tag text-[10px] text-[#3F7A2E] tracking-wider shrink-0 font-bold">
                              +{r.count30d} · 30D
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-3 pl-[22px]">
                            <span className="font-mono-tag text-[9px] text-[#78584B]">
                              {r.count} TOTAL · {pctOfTotal}% ADDED THIS MONTH
                            </span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </motion.div>
          ) : (
            <motion.div
              variants={FADE_UP}
              className="rounded-sm border border-[#1A0F0A]/12 bg-[#FBF4E4] p-5"
              data-testid="heatmap-by-region"
            >
              <PanelHeader
                kicker="ALL FIVE"
                title="By region"
              />
              <ul className="space-y-2.5 mt-4">
                {regionStats.map((r, idx) => {
                  const w = (r.count / maxRegionCount) * 100;
                  return (
                    <li key={r.region}>
                      <button
                        type="button"
                        onClick={() => flyTo(REGION_VIEWS[r.region])}
                        data-testid={`region-tally-${r.region.replace(/ /g, "-").toLowerCase()}`}
                        className="w-full text-left group"
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-xs text-[#4D3F35] group-hover:text-[#6B0F1A] transition-colors truncate">
                            {r.region}
                          </span>
                          <span className="font-mono-tag text-[10px] text-[#1A0F0A] tracking-wider shrink-0">
                            {r.count}
                          </span>
                        </div>
                        <div className="h-1 rounded-full bg-[#1A0F0A]/8 overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${w}%` }}
                            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.1 + idx * 0.04 }}
                            className="h-full rounded-full bg-[#6B0F1A]"
                          />
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          )}
      </motion.div>


      {/* APPLICANT DETAILS MODAL — opens when admin clicks a name inside
          a map popup. Read-only profile card with contact + score + notes
          + resume link. Mirrors the drawer card in ApplicantsAdmin. */}
      <Dialog
        open={!!selectedApplicant}
        onOpenChange={(o) => { if (!o) setSelectedApplicant(null); }}
      >
        <DialogContent
          className="bg-[#FBF4E4] border-[#1A0F0A]/12 text-[#1A0F0A] rounded-sm max-w-md p-0 overflow-hidden"
          data-testid="applicant-details-modal"
        >
          {selectedApplicant && (
            <ApplicantDetailsCard applicant={selectedApplicant} entityLabel={ent.s} />
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

// ─── Applicant profile card rendered inside the heatmap detail modal. ───
function ApplicantDetailsCard({ applicant: a, entityLabel = "applicant" }) {
  const stars = Array.from({ length: 5 }, (_, i) => i < (a.score || 0));
  const initials = (a.name || "?")
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const created = a.created_at ? new Date(a.created_at) : null;
  const daysSince = created
    ? Math.max(0, Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24)))
    : null;
  return (
    <div className="flex flex-col">
      {/* Editorial header — oxblood band with initials + name + stage */}
      <div className="relative bg-gradient-to-br from-[#6B0F1A] to-[#8A1424] text-[#F5EBD9] px-6 py-5">
        <div className="flex items-center gap-4">
          <div
            className="w-14 h-14 rounded-full bg-[#F5EBD9]/12 border border-[#F5EBD9]/25 grid place-items-center font-display text-lg font-extrabold tracking-tight"
            aria-hidden
          >
            {initials || "?"}
          </div>
          <div className="min-w-0 flex-1">
            <DialogHeader className="p-0 space-y-1">
              <DialogTitle
                className="font-display text-2xl font-extrabold tracking-tight text-[#F5EBD9] leading-tight truncate"
                data-testid="applicant-details-name"
              >
                {a.name || `Unnamed ${entityLabel}`}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-1 flex items-center gap-2 flex-wrap font-mono-tag text-[10px] tracking-[0.2em] text-[#B08D57]">
              <span
                className="inline-block px-2 py-0.5 rounded-sm bg-[#F5EBD9]/12 border border-[#F5EBD9]/20 text-[#F5EBD9]"
                data-testid="applicant-details-stage"
              >
                {(a.stage || "—").toUpperCase()}
              </span>
              {(a.applied_role === "pilot" || a.applied_role === "photographer") && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#F5EBD9]/12 border border-[#B08D57]/40 text-[#B08D57]"
                  data-testid="applicant-details-role"
                >
                  {a.applied_role === "pilot" ? "PILOT" : "PHOTOG"}
                </span>
              )}
              {daysSince != null && (
                <span>{daysSince === 0 ? "APPLIED TODAY" : `APPLIED ${daysSince}D AGO`}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
        {/* Score */}
        <div>
          <div className="font-mono-tag text-[10px] tracking-[0.25em] text-[#78584B] mb-1.5">SCORE</div>
          <div className="inline-flex items-center gap-0.5" data-testid="applicant-details-score">
            {stars.map((filled, i) => (
              <Star
                key={i}
                className={`w-4 h-4 ${filled ? "fill-[#B08D57] text-[#B08D57]" : "text-[#1A0F0A]/20"}`}
                strokeWidth={1.5}
              />
            ))}
            <span className="ml-2 font-mono-tag text-[10px] tracking-wider text-[#78584B]">
              {a.score || 0}/5
            </span>
          </div>
        </div>

        {/* Contact */}
        <dl className="grid grid-cols-1 gap-3 text-sm">
          {a.email && (
            <div className="flex items-start gap-2.5">
              <Mail className="w-4 h-4 text-[#B08D57] mt-0.5 shrink-0" strokeWidth={1.75} />
              <div className="min-w-0">
                <dt className="font-mono-tag text-[9px] tracking-[0.2em] text-[#78584B]">EMAIL</dt>
                <dd className="text-[#1A0F0A] truncate">
                  <a
                    href={`mailto:${a.email}`}
                    className="hover:text-[#6B0F1A] hover:underline"
                    data-testid="applicant-details-email"
                  >
                    {a.email}
                  </a>
                </dd>
              </div>
            </div>
          )}
          {a.phone && (
            <div className="flex items-start gap-2.5">
              <Phone className="w-4 h-4 text-[#B08D57] mt-0.5 shrink-0" strokeWidth={1.75} />
              <div className="min-w-0">
                <dt className="font-mono-tag text-[9px] tracking-[0.2em] text-[#78584B]">PHONE</dt>
                <dd className="text-[#1A0F0A]">
                  <a href={`tel:${a.phone}`} className="hover:text-[#6B0F1A] hover:underline">
                    {a.phone}
                  </a>
                </dd>
              </div>
            </div>
          )}
          <div className="flex items-start gap-2.5">
            <MapPin className="w-4 h-4 text-[#B08D57] mt-0.5 shrink-0" strokeWidth={1.75} />
            <div className="min-w-0">
              <dt className="font-mono-tag text-[9px] tracking-[0.2em] text-[#78584B]">LOCATION</dt>
              <dd className="text-[#1A0F0A]">
                {a.resolved_city || a.desired_location || a.region || "—"}
                {a.zip_code && (
                  <span className="ml-1.5 font-mono-tag text-[10px] tracking-wider text-[#78584B]">
                    · {a.zip_code}
                  </span>
                )}
              </dd>
              {a.desired_travel_distance && (
                <dd className="text-xs text-[#78584B] mt-0.5">
                  Willing to travel {a.desired_travel_distance}
                </dd>
              )}
            </div>
          </div>
        </dl>

        {/* Resume */}
        {a.resume && (
          <div>
            <div className="font-mono-tag text-[10px] tracking-[0.25em] text-[#78584B] mb-1.5">RESUME · PORTFOLIO</div>
            <a
              href={a.resume}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="applicant-details-resume"
              className="inline-flex items-center gap-1.5 text-sm text-[#6B0F1A] hover:underline break-all"
            >
              <ExternalLink className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
              <span className="truncate">{a.resume}</span>
            </a>
          </div>
        )}

        {/* Notes */}
        {a.notes && (
          <div>
            <div className="font-mono-tag text-[10px] tracking-[0.25em] text-[#78584B] mb-1.5">NOTES</div>
            <p
              className="text-sm text-[#4D3F35] whitespace-pre-wrap leading-relaxed rounded-sm bg-[#F5EBD9] border-l-2 border-[#B08D57] px-3 py-2"
              data-testid="applicant-details-notes"
            >
              {a.notes}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Sub-components — all cheap presentational bits kept below so they don't
 * pollute the main file's public surface but are still colocated.
 * ---------------------------------------------------------------------- */

// Inline KPI chip used by the hero band's stat strip.
function KpiChip({ icon: Icon, label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="min-w-[110px] px-3 py-2 flex flex-col justify-center"
    >
      <div className="flex items-center gap-1.5 font-mono-tag text-[9px] tracking-[0.15em] text-[#78584B]">
        <Icon className="w-3 h-3 text-[#6B0F1A]" strokeWidth={2} />
        {label}
      </div>
      <div
        className="font-display text-xl sm:text-2xl font-extrabold tracking-tighter mt-0.5 leading-none"
        style={{ color: accent || "#1A0F0A" }}
      >
        {value}
      </div>
    </div>
  );
}

// Small labeled cluster in the toolbar so groups feel visually related.
function ToolGroup({ label, icon: Icon, children }) {
  return (
    <div className="inline-flex items-center gap-2">
      <span className="inline-flex items-center gap-1 font-mono-tag text-[9px] tracking-[0.15em] text-[#78584B]">
        {Icon ? <Icon className="w-3 h-3" strokeWidth={2} /> : null}
        {label}
      </span>
      {children}
    </div>
  );
}

// Segmented control container + button (used for basemap + mode).
function SegBar({ children }) {
  return (
    <div className="inline-flex rounded-sm border border-[#1A0F0A]/15 overflow-hidden bg-white/40">
      {children}
    </div>
  );
}

function SegBtn({ active, onClick, testId, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={title}
      className={`px-2.5 h-8 inline-flex items-center gap-1 text-[10px] font-mono-tag tracking-wide transition-colors ${
        active
          ? "bg-[#6B0F1A] text-[#F5EBD9]"
          : "bg-transparent text-[#4D3F35] hover:bg-[#1A0F0A]/6"
      }`}
    >
      {children}
    </button>
  );
}

// Editorial header for each sidebar panel — kicker + title on two lines.
function PanelHeader({ icon: Icon, kicker, title, accent }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <div>
        <div className="font-mono-tag text-[9px] tracking-[0.2em]" style={{ color: accent || "#6B0F1A" }}>
          {kicker}
        </div>
        <h3 className="font-display text-lg font-bold tracking-tight text-[#1A0F0A] mt-0.5 leading-none">
          {title}
        </h3>
      </div>
      {Icon ? <Icon className="w-4 h-4 shrink-0" strokeWidth={2} style={{ color: accent || "#6B0F1A" }} /> : null}
    </div>
  );
}

// Rank badge for the top-cities list — gold / silver / bronze medal for
// the first three, otherwise a small numeric chip.
function RankBadge({ rank }) {
  if (rank === 1) {
    return (
      <div
        aria-hidden
        className="w-8 h-8 rounded-full shrink-0 grid place-items-center shadow-[0_2px_8px_-2px_rgba(176, 141, 87,0.6),inset_0_0_0_1px_#B08D57]"
        style={{ background: "linear-gradient(135deg,#F5EBD9,#B08D57 45%,#B08D57)" }}
      >
        <Crown className="w-3.5 h-3.5 text-[#1A0F0A]" strokeWidth={2.25} />
      </div>
    );
  }
  if (rank === 2 || rank === 3) {
    const isSilver = rank === 2;
    return (
      <div
        aria-hidden
        className="w-8 h-8 rounded-full shrink-0 grid place-items-center shadow-[inset_0_0_0_1px_rgba(26,15,10,0.15)]"
        style={{
          background: isSilver
            ? "linear-gradient(135deg,#F0EDE5,#B8AFA0)"
            : "linear-gradient(135deg,#D6A67A,#8B5A34)",
        }}
      >
        <Medal className={`w-3.5 h-3.5 ${isSilver ? "text-[#4D3F35]" : "text-[#F5EBD9]"}`} strokeWidth={2.25} />
      </div>
    );
  }
  return (
    <div className="w-8 h-8 rounded-full shrink-0 grid place-items-center bg-[#1A0F0A]/6 border border-[#1A0F0A]/10">
      <span className="font-mono-tag text-[10px] font-bold text-[#78584B] tracking-wider">{rank}</span>
    </div>
  );
}

function EmptyPanel({ text }) {
  return (
    <div className="mt-4 rounded-sm border border-dashed border-[#1A0F0A]/15 p-4 text-center">
      <Award className="w-5 h-5 text-[#1A0F0A]/25 mx-auto mb-2" strokeWidth={1.5} />
      <p className="text-xs text-[#78584B]">{text}</p>
    </div>
  );
}
